import {
  Banner, Badge, BlockStack, Button, ButtonGroup, Checkbox, DataTable,
  InlineStack, Layout, Page, Text, TextField,
} from "@shopify/polaris";
import { useCallback, useEffect, useMemo, useState } from "react";
import { admin_accounts, create_admin_account, update_admin_account } from "../api";

const groups = [
  { title: "Shop settings", permissions: ["shops.view", "shops.manage"] },
  { title: "Store performance", permissions: ["performance.view", "performance.export"] },
  { title: "Logs", permissions: ["logs.view", "logs.delete"] },
  { title: "Account administration", permissions: ["accounts.manage"] },
];

const emptyDraft = () => ({ user: "", password: "", permissions: [], grantable_permissions: [], is_delete: 0 });

export default function AdminAccounts({ context, permissions }) {
  const [accounts, setAccounts] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");
  const owner = Boolean(context.is_owner);
  const labels = useMemo(() => context.permission_labels || {}, [context.permission_labels]);
  const grantableByMe = new Set(context.grantable_permissions || []);
  const dependencies = { "shops.manage": "shops.view", "performance.export": "performance.view", "logs.delete": "logs.view" };

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await admin_accounts({});
      if (response.status === 200) setAccounts(response.data || []);
      else setMessage({ tone: "critical", text: response.msg || "Unable to load accounts." });
    } catch (requestError) {
      setMessage({ tone: "critical", text: requestError?.response?.data?.msg || "Unable to load accounts." });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const openEdit = (account) => {
    setError("");
    setMessage(null);
    setDraft({ ...account, password: "", permissions: [...(account.permissions || [])], grantable_permissions: [...(account.grantable_permissions || [])] });
  };
  const toggle = (field, permission, checked) => setDraft((current) => {
    const values = new Set(current[field] || []);
    if (checked) values.add(permission); else values.delete(permission);
    if (checked && dependencies[permission]) values.add(dependencies[permission]);
    if (!checked) Object.entries(dependencies).forEach(([child, parent]) => parent === permission && values.delete(child));
    if (field === "permissions" && permission === "accounts.manage" && !checked) return { ...current, permissions: [...values], grantable_permissions: [] };
    return { ...current, [field]: [...values] };
  });

  const save = async () => {
    setError("");
    if (draft.user.trim().length < 2) return setError("Enter a login name with at least 2 characters.");
    if (draft.id == null && draft.password.length < 12) return setError("Set an initial password of at least 12 characters.");
    if (!owner && draft.permissions.includes("accounts.manage")) return setError("Only the system owner can authorize account administration.");
    if (!owner && draft.permissions.some((permission) => !grantableByMe.has(permission))) return setError("This account includes a permission outside your grantable access.");
    setSaving(true);
    try {
      const payload = { ...draft, user: draft.user.trim() };
      const response = draft.id == null ? await create_admin_account(payload) : await update_admin_account(payload);
      if (response.status !== 200) return setError(response.msg || "Unable to save account.");
      setDraft(null);
      setMessage({ tone: "success", text: draft.id == null ? "Account created." : "Account permissions updated." });
      await loadAccounts();
    } catch (requestError) {
      setError(requestError?.response?.data?.msg || requestError?.response?.data?.message || "Unable to save account.");
    } finally { setSaving(false); }
  };

  const rows = useMemo(() => accounts.map((account) => [
    account.user,
    <Badge tone={account.is_delete ? "critical" : "success"}>{account.is_delete ? "Disabled" : "Active"}</Badge>,
    account.is_owner ? "System owner" : (account.permissions || []).map((permission) => labels[permission] || permission).join(", ") || "No features authorized",
    <Button onClick={() => openEdit(account)} disabled={account.is_owner || account.id === context.admin.id}>Edit access</Button>,
  ]), [accounts, context.admin.id, labels]);

  return (
    <Page title="Accounts & permissions" subtitle="Grant features directly to each account. Store access covers all shops.">
      <Layout>
        {message && <Layout.Section><Banner tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Banner></Layout.Section>}
        <Layout.Section>
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" tone="subdued">A/B shop links are visible to the system owner only.</Text>
            <Button variant="primary" onClick={() => { setError(""); setMessage(null); setDraft(emptyDraft()); }}>Create account</Button>
          </InlineStack>
        </Layout.Section>
        <Layout.Section>
          <DataTable columnContentTypes={["text", "text", "text", "text"]} headings={["Login", "Status", "Authorized features", ""]} rows={rows} loading={loading} />
        </Layout.Section>
      </Layout>

      {draft && <div role="presentation" className="admin-account-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !saving && setDraft(null)}>
        <section className="admin-account-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-account-title">
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center"><Text as="h2" variant="headingLg" id="admin-account-title">{draft.id == null ? "Create admin account" : `Edit access · ${draft.user}`}</Text><Button variant="plain" onClick={() => setDraft(null)} disabled={saving} accessibilityLabel="Close">×</Button></InlineStack>
            {error && <Banner tone="critical">{error}</Banner>}
            <TextField label="Login name" value={draft.user} onChange={(user) => setDraft((current) => ({ ...current, user }))} autoComplete="username" disabled={draft.id != null} />
            {draft.id == null && <TextField label="Initial password" type="password" value={draft.password} onChange={(password) => setDraft((current) => ({ ...current, password }))} autoComplete="new-password" minLength={12} helpText="At least 12 characters." />}
            {draft.id != null && <TextField label="Reset password (optional)" type="password" value={draft.password} onChange={(password) => setDraft((current) => ({ ...current, password }))} autoComplete="new-password" minLength={12} helpText="Leave blank to keep the current password. A new password revokes existing sessions." />}
            <Banner>A selected permission grants access to all existing and future shops.</Banner>
            {groups.map((group) => <div key={group.title} className="admin-account-permission-group"><Text as="h3" variant="headingSm">{group.title}</Text>{group.permissions.map((permission) => <Checkbox key={permission} label={labels[permission] || permission} checked={(draft.permissions || []).includes(permission)} disabled={!owner && (permission === "accounts.manage" || !grantableByMe.has(permission))} onChange={(checked) => toggle("permissions", permission, checked)} />)}</div>)}
            {owner && draft.permissions.includes("accounts.manage") && <div className="admin-account-permission-group"><Text as="h3" variant="headingSm">Permissions this account can grant</Text><Text as="p" tone="subdued">It can only grant business permissions assigned to its own account.</Text>{permissions.filter((permission) => permission !== "accounts.manage").map((permission) => <Checkbox key={permission} label={labels[permission] || permission} checked={(draft.grantable_permissions || []).includes(permission)} disabled={!(draft.permissions || []).includes(permission)} onChange={(checked) => toggle("grantable_permissions", permission, checked)} />)}</div>}
            {draft.id != null && <Checkbox label="Account enabled" checked={!draft.is_delete} onChange={(checked) => setDraft((current) => ({ ...current, is_delete: checked ? 0 : 1 }))} />}
            <InlineStack align="end"><ButtonGroup><Button onClick={() => setDraft(null)} disabled={saving}>Cancel</Button><Button variant="primary" onClick={save} loading={saving}>Save</Button></ButtonGroup></InlineStack>
          </BlockStack>
        </section>
      </div>}
    </Page>
  );
}
